export type ProjectDetailPrimaryTab = 'overview' | 'execution' | 'decisions' | 'assets' | 'people';

export type ProjectTab = ProjectDetailPrimaryTab;

export type ProjectTabParam =
  | ProjectDetailPrimaryTab
  | 'governance'
  | 'deal-room'
  | 'financials'
  | 'activity'
  | 'capital'
  | 'documents'
  | 'companies'
  | 'participants'
  | 'timeline';

export const PROJECT_DETAIL_PRIMARY_TAB_DEFINITIONS = [
  { id: 'overview', label: 'Overview', icon: 'layoutDashboard' },
  { id: 'execution', label: 'Milestones', icon: 'milestone' },
  { id: 'decisions', label: 'Decisions', icon: 'scale' },
  { id: 'assets', label: 'Assets', icon: 'package' },
  { id: 'people', label: 'Participants', icon: 'users' },
] as const;

/** Preserve old deep links while presenting the reduced information architecture. */
export function resolveProjectDetailPrimaryTab(tab: ProjectTabParam | null): ProjectDetailPrimaryTab {
  if (tab === 'documents' || tab === 'deal-room' || tab === 'assets') return 'assets';
  if (tab === 'companies' || tab === 'participants' || tab === 'people') return 'people';
  if (tab === 'timeline' || tab === 'execution' || tab === 'governance' || tab === 'capital') return 'execution';
  if (tab === 'decisions') return 'decisions';
  return 'overview';
}

/**
 * Keep Project Details URLs canonical as users move between primary sections.
 * Nested state belongs only to its owning section; retaining it elsewhere makes
 * copied links misleading and can reopen stale views on a later visit.
 */
export function buildProjectDetailSearchParams(
  current: URLSearchParams,
  tab: ProjectTabParam,
): URLSearchParams {
  const primaryTab = resolveProjectDetailPrimaryTab(tab);
  const next = new URLSearchParams(current);

  // Preserve the intent of supported legacy deep links while replacing their
  // top-level destination with the current focused information model.
  if (tab === 'documents') next.set('assetTab', 'documents');
  if (tab === 'deal-room') next.set('assetTab', 'dataRooms');
  if (tab === 'companies') next.set('participantTab', 'companies');
  if (tab === 'participants') next.set('participantTab', 'team');

  if (primaryTab === 'overview') next.delete('tab');
  else next.set('tab', primaryTab);

  if (primaryTab !== 'assets') next.delete('assetTab');
  if (primaryTab !== 'people') {
    next.delete('participantTab');
    next.delete('participantFocus');
  }
  if (primaryTab !== 'execution') {
    next.delete('milestone');
    next.delete('intent');
    next.delete('executionView');
  }
  if (primaryTab !== 'overview' && primaryTab !== 'decisions') next.delete('decision');

  return next;
}
